import { cx } from "@suse-o11y/class-variance";
import React from "react";

import { Dropdown, type DropdownProps } from "~/components/dropdown";
import { Truncated } from "~/components/truncated";

import { Menu, type MenuClickParams, MenuItem } from "../react/menu/Menu";

export interface DropdownMenuProps<TItem> {
	menuTitle?: string;
	menuItems: Array<TItem>;
	onChange: (item: TItem) => void;
	initialSelectedKey?: string;
	selectedKey?: string;
	children: React.ReactNode;
	toKey: (item: TItem) => string;
	toTitle: (item: TItem) => string;
	disabled?: boolean;
}

export const DropdownMenu = <TItem,>({
	disabled,
	children,
	menuTitle,
	menuItems,
	onChange,
	initialSelectedKey,
	selectedKey,
	toKey,
	toTitle,
	...rest
}: DropdownMenuProps<TItem> & Partial<DropdownProps>): React.ReactNode => {
	const handleClick = ({ key }: MenuClickParams) => {
		const target = menuItems.find((item) => key === toKey(item));

		if (target != null) {
			onChange(target);
		}
	};

	const menu = (
		<Menu
			className="w-[240px] max-w-[300px] p-4 font-medium text-sm"
			data-testid="dropdown-menu"
			defaultSelectedKeys={[initialSelectedKey ?? toKey(menuItems[0])]}
			disabled={disabled}
			onClick={handleClick}
			selectable
			selectedKeys={selectedKey == null ? undefined : [selectedKey]}
		>
			{menuTitle && <p className="mt-1 mb-4 font-medium text-sm">{menuTitle}</p>}
			{menuItems.map((item) => (
				<MenuItem
					className={cx(
						"bg-transparent p-0 text-13 hover:text-primary-500",
						"[&.ant-dropdown-menu-item-selected]:font-medium [&.ant-dropdown-menu-item-selected]:text-primary-600",
						"[&.ant-dropdown-menu-item:hover]:bg-transparent [&.ant-dropdown-menu-item:hover]:text-primary-500",
						"[&.ant-dropdown-menu-item:last-child_p]:mb-0",
						"[&_.ant-dropdown-menu-title-content]:w-full",
					)}
					key={toKey(item)}
					title={toTitle(item)}
				>
					<Truncated className="text-13" el="p">
						{toTitle(item)}
					</Truncated>
				</MenuItem>
			))}
		</Menu>
	);

	return (
		<Dropdown align={{ targetOffset: [0, 4] }} disabled={disabled} overlay={menu} trigger={["click"]} {...rest}>
			{children}
		</Dropdown>
	);
};
